/**
 * Slice 16 / 35 — Job Start (pre-visit). Photo → name → Keys & entry open → fat Start.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, Home, Loader2 } from "lucide-react";
import { formatScheduledDate } from "@/lib/dateUtils";
import type { CurrentCleaningTask } from "@/types/cleaning";
import type { PropertyAccessInstructions } from "@/hooks/useProperties";
import {
  InventoryReorderPickupBanner,
} from "@/components/cleaner/InventoryReorderPickupBanner";
import type { PendingReorderForPickup } from "@/hooks/useInventoryReorders";
import { cn } from "@/lib/utils";
import { CLEANER_SIMPLE_STEP_SHELL_MIN, CLEANER_SIMPLE_CTA_FOOTER } from "@/lib/turnoverChrome";
import { chipClassName, type TurnoverChipState } from "@/lib/turnoverWindow";
import { useTranslation } from "@/hooks/useTranslation";
import { useLocationIntro } from "@/hooks/useLocationIntro";
import { OfflineLine } from "@/components/cleaner/OfflineLine";
import { LocationIntroStep } from "@/components/cleaner/LocationIntroStep";
import { fetchStampSettings } from "@/lib/stampOutbox";
import { isGeolocationApiAvailable } from "@/lib/geolocation";

const ENTRY_METHOD_KEYS: Record<string, string> = {
  lockbox: "entryLockbox",
  keypad: "entryKeypad",
  key: "entryPhysicalKey",
  smart_lock: "entrySmartLock",
  concierge: "entryConcierge",
  other: "entryOther",
};

interface TaskPreStartViewProps {
  task: CurrentCleaningTask;
  propertyName: string | null;
  propertyAddress: string | null;
  propertyPhotoUrl: string | null;
  accessInstructions: PropertyAccessInstructions | null;
  onStart: () => void;
  onBack: () => void;
  isStarting?: boolean;
  /** Defaults to "Start" (Slice 16). */
  startButtonLabel?: string;
  startDisabled?: boolean;
  startBlockedHint?: string | null;
  t?: (key: string) => string;
  pendingReorders?: PendingReorderForPickup[] | null;
  cleanerId?: string | null;
  propertyId?: string | null;
  /** Optional override e.g. "Same day · Sep 15" / "Turnover · Sep 15–16" */
  windowLabel?: string | null;
  windowTimes?: string | null;
  /** Slice 12c muted second line under the primary clock */
  windowTimesSecondary?: string | null;
  windowTimesTone?: "default" | "teal" | "amber" | "muted" | null;
  guestLeft?: boolean;
  waitingOnGuest?: boolean;
  sameDay?: boolean;
  tightWindow?: boolean;
  windowChip?: string | null;
}

function splitLocation(raw: string | null | undefined): { headline: string; detail: string | null } {
  const trimmed = (raw ?? "").trim();
  if (!trimmed) return { headline: "", detail: null };
  const comma = trimmed.indexOf(",");
  if (comma > 0 && comma < 56) {
    return {
      headline: trimmed.slice(0, comma).trim(),
      detail: trimmed.slice(comma + 1).trim() || null,
    };
  }
  return { headline: trimmed, detail: null };
}

export function TaskPreStartView({
  task,
  propertyName,
  propertyAddress,
  propertyPhotoUrl,
  accessInstructions,
  onStart,
  onBack,
  isStarting = false,
  startButtonLabel = "Start",
  startDisabled = false,
  startBlockedHint = null,
  t: tProp,
  pendingReorders = null,
  cleanerId = null,
  propertyId = null,
  windowLabel = null,
  windowTimes = null,
  windowTimesSecondary = null,
  windowTimesTone: _windowTimesTone = null,
  guestLeft = false,
  waitingOnGuest = false,
  windowChip = null,
}: TaskPreStartViewProps) {
  const { t: tHook, tDynamic } = useTranslation();
  const t = tProp ?? tHook;
  const locationIntro = useLocationIntro();
  const [showLocationIntro, setShowLocationIntro] = useState(false);
  const { data: stampSettings } = useQuery({
    queryKey: ["stamp-settings", task.property_id],
    queryFn: () => fetchStampSettings(task.property_id),
    staleTime: 60_000,
  });
  const stampsOn = stampSettings ? stampSettings.enabled : true;
  const handleStartClick = () => {
    if (stampsOn && !locationIntro.seen && isGeolocationApiAvailable()) {
      setShowLocationIntro(true);
      return;
    }
    onStart();
  };
  const displayName = propertyName || t("property");

  const CHIP_LABEL_KEYS: Record<string, string> = {
    "Same day": "sameDay",
    Open: "chipOpen",
    Upcoming: "chipUpcoming",
    Overdue: "chipOverdue",
    "In progress": "chipInProgress",
    Done: "chipDone",
    Updated: "chipUpdated",
    "Test clean": "chipTestClean",
    "Guest left": "guestLeft",
    "Waiting on guest": "waitingOnGuest",
  };

  const keysBlock = useMemo(() => {
    const { headline, detail } = splitLocation(accessInstructions?.entry_location);
    const methodKey = accessInstructions?.entry_method
      ? ENTRY_METHOD_KEYS[accessInstructions.entry_method]
      : null;
    const method = methodKey
      ? t(methodKey)
      : accessInstructions?.entry_method || null;
    const methodLine = [method, detail].filter(Boolean).join(" · ") || null;
    const code = accessInstructions?.entry_code?.trim() || null;
    const secondary = [
      accessInstructions?.unit_location?.trim()
        ? { label: t("unitLabel"), value: accessInstructions.unit_location.trim() }
        : null,
      accessInstructions?.parking_instructions?.trim()
        ? { label: t("parkingLabel"), value: accessInstructions.parking_instructions.trim() }
        : null,
      accessInstructions?.supply_closet_info?.trim()
        ? { label: t("suppliesLabel"), value: accessInstructions.supply_closet_info.trim() }
        : null,
    ].filter(Boolean) as { label: string; value: string }[];
    return { headline, methodLine, code, secondary, hasAny: !!(headline || method || code || secondary.length) };
  }, [accessInstructions, t]);

  const turnoverLine =
    windowLabel?.trim() ||
    t("turnoverDate").replace("{{date}}", formatScheduledDate(task.scheduled_date, "MMM d"));

  const guestLeftChip = guestLeft;
  const waitingChip = Boolean(waitingOnGuest);
  const chipKey = CHIP_LABEL_KEYS[windowChip ?? ""];
  const chipLabel = guestLeftChip
    ? t("guestLeft")
    : waitingChip
      ? t("waitingOnGuest")
      : chipKey
        ? t(chipKey)
        : windowChip
          ? tDynamic(windowChip)
          : null;
  const chipClass = guestLeftChip
    ? "bg-[#eaf6f4] text-[#15776d]"
    : waitingChip
      ? "bg-[#fdf2da] text-[#9a6700]"
      : windowChip
        ? chipClassName(windowChip as TurnoverChipState)
        : "";

  const startingLabel = t("starting");
  const ctaLabel = isStarting
    ? startingLabel
    : waitingChip && startButtonLabel === "Start"
      ? t("startAnyway")
      : startButtonLabel === "Start"
        ? t("startClean")
        : startButtonLabel;
  const jobsLabel = t("myJobs");

  const detailPrimary = windowTimes?.trim() || null;
  const detailSecondary = windowTimesSecondary?.trim() || null;

  // Shown after every hook above has run, so the number of hooks never changes between renders.
  if (showLocationIntro) {
    return (
      <LocationIntroStep
        onContinue={() => {
          setShowLocationIntro(false);
          void locationIntro.markSeen();
          onStart();
        }}
      />
    );
  }
  return (
    <div
      className={cn(
        CLEANER_SIMPLE_STEP_SHELL_MIN,
        "font-[family-name:var(--font-sans,'DM_Sans',sans-serif)] text-[#0f1720]",
      )}
    >
      <div className="flex shrink-0 items-center gap-3 px-5 pt-3">
        <button
          type="button"
          onClick={onBack}
          className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-xl bg-[#f4f7f9] text-[#475569] transition-opacity active:opacity-80"
          aria-label={jobsLabel}
        >
          <ChevronLeft className="h-[17px] w-[17px]" strokeWidth={2.6} />
        </button>
        <span className="text-[15px] font-bold text-[#64748b]">{jobsLabel}</span>
      </div>

      <div className="px-5 pt-3 empty:hidden">
        <OfflineLine />
      </div>

      {pendingReorders && pendingReorders.length > 0 && cleanerId && propertyId ? (
        <div className="mt-3 px-5">
          <InventoryReorderPickupBanner
            reorders={pendingReorders}
            cleanerId={cleanerId}
            propertyId={propertyId}
          />
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-y-auto px-5 pt-5">
        <div
          className={cn(
            "relative aspect-[16/10] w-full shrink-0 overflow-hidden rounded-[18px] border-[1.5px] border-[#e6edf1] sm:h-[208px] sm:aspect-auto",
            "bg-[#eff4f7]",
          )}
        >
          {propertyPhotoUrl ? (
            <img
              src={propertyPhotoUrl}
              alt={displayName}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center gap-3">
              <Home className="h-[38px] w-[38px] text-[#a5b3bf]" strokeWidth={1.9} />
              <span className="font-mono text-xs text-[#64748b]">{t("noListingPhoto")}</span>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <h1 className="text-[23px] font-bold leading-[1.15] tracking-[-0.4px] text-[#0f1720]">
            {tDynamic(displayName)}
          </h1>
          <p className="text-[13.5px] font-medium text-[#64748b]">
            {detailPrimary
              ? tDynamic(
                  `${detailPrimary}${detailSecondary ? ` · ${detailSecondary}` : ""}`,
                )
              : tDynamic(turnoverLine)}
          </p>
          {chipLabel ? (
            <div className="mt-1.5 flex">
              <span
                className={cn(
                  "inline-flex items-center rounded-full px-3.5 py-1.5 text-[13px] font-bold tracking-[0.3px]",
                  chipClass,
                )}
              >
                {chipLabel}
              </span>
            </div>
          ) : null}
          {propertyAddress ? (
            <p className="mt-1 text-[14px] font-medium text-[#8494a2]">{tDynamic(propertyAddress)}</p>
          ) : null}
        </div>

        {keysBlock.hasAny ? (
          <div className="flex flex-col gap-3 rounded-[18px] border-[1.5px] border-[#cfe6e2] bg-white px-[17px] py-4">
            <div className="text-[11px] font-bold tracking-[0.8px] text-[#15776d]">{t("keysAndEntry")}</div>
            {keysBlock.headline ? (
              <div className="flex flex-col gap-1">
                <div className="text-[20px] font-bold leading-snug tracking-[-0.3px] text-[#0f1720]">
                  {tDynamic(keysBlock.headline)}
                </div>
                {keysBlock.methodLine ? (
                  <div className="text-[13.5px] font-medium text-[#64748b]">
                    {tDynamic(keysBlock.methodLine)}
                  </div>
                ) : null}
              </div>
            ) : keysBlock.methodLine ? (
              <div className="text-[16px] font-bold text-[#0f1720]">{tDynamic(keysBlock.methodLine)}</div>
            ) : null}
            {keysBlock.code ? (
              <div className="flex items-center gap-3 rounded-xl bg-[#f1f7f6] px-3.5 py-2.5">
                <div className="text-[11px] font-bold tracking-[0.7px] text-[#15776d]">{t("code")}</div>
                <div className="font-mono text-[26px] font-bold tracking-[3px] text-[#0d453f]">
                  {keysBlock.code}
                </div>
              </div>
            ) : null}
            {keysBlock.secondary.length > 0 ? (
              <div className="flex flex-col gap-1.5 pt-0.5">
                {keysBlock.secondary.map((row) => (
                  <div key={row.label} className="flex gap-2.5">
                    <div className="w-[74px] shrink-0 text-[11.5px] font-bold tracking-[0.3px] text-[#8494a2]">
                      {row.label}
                    </div>
                    <div className="flex-1 text-[13.5px] font-medium text-[#475569]">
                      {tDynamic(row.value)}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ) : (
          <div className="flex flex-col gap-1.5 rounded-[18px] border border-dashed border-[#c6d2d9] bg-white px-[17px] py-4">
            <div className="text-[11px] font-bold tracking-[0.8px] text-[#8494a2]">{t("keysAndEntry")}</div>
            <div className="text-[17px] font-bold tracking-[-0.2px] text-[#64748b]">{t("noKeyLocationSet")}</div>
            <div className="text-[13px] font-medium leading-snug text-[#8494a2]">
              {t("askHostWhenThereStart")}
            </div>
          </div>
        )}
      </div>

      <div className={cn(CLEANER_SIMPLE_CTA_FOOTER, "mt-auto border-t border-transparent bg-white")}>
        {startDisabled && startBlockedHint ? (
          <p className="mb-2 text-center text-sm text-[#64748b]">{startBlockedHint}</p>
        ) : null}
        <button
          type="button"
          onClick={handleStartClick}
          disabled={isStarting || startDisabled}
          className={cn(
            "flex h-[58px] w-full items-center justify-center gap-3 rounded-[15px] text-[18px] font-bold text-white transition-colors",
            isStarting || startDisabled
              ? "bg-[#1f958a] opacity-90 disabled:opacity-50"
              : "bg-[#24a89a] hover:bg-[#1f958a] active:bg-[#1b8378]",
          )}
        >
          {isStarting ? <Loader2 className="h-5 w-5 animate-spin text-white" /> : null}
          {ctaLabel}
        </button>
      </div>
    </div>
  );
}
